import {
  Box,
  Typography,
  List,
  ListItemText,
  Paper,
  Grid,
  ListItemIcon,
  ListItem,
} from '@mui/material';

import React, { useMemo, useState } from 'react';
import {
  ArrowUpward as LoadingIcon,
  ArrowDownward as DumpingIcon,
  MultipleStop as MovingIcon,
  HourglassTop as WaitIcon,
  ArrowRightAlt as ArrowIcon,
  TaskAlt,
  Download,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  clearAcknowledgedAssets,
  setPlanDispatched,
} from 'store/fleet-planning.slice';
import {
  aggregateSchedulePlanByBumperNumber,
  collapseTravelSegments,
  formatLocation,
  mapSchedulePlanMetricsByBumper,
  safeNumber,
} from 'views/FleetPlanner/utils/dataFormatters';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
// import { FleetInputsSummary } from '../components/FleetInputSummary';
import { exportPlanDataToExcel } from 'views/FleetPlanner/utils/exportToExcel';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { PlanComparison } from '../components/PlanComparisonCard';
import { truncateTo2dp } from 'utils/helpers/general';
import FleetPlanningHandler from 'handlers/fleet-planning.handler';
import { type ExternalUser, type InternalUser } from 'types/user.types';
import useCustomerInfo from 'hooks/useCustomerInfo';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getCurrentCustomerShiftInfo } from 'utils/helpers/customerShiftHelpers';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

const getActionDetails = (action: string) => {
  switch (action) {
    case 'loading':
      return {
        icon: <LoadingIcon />,
        color: 'success.main',
        label: 'Loading',
      };
    case 'dumping':
      return {
        icon: <DumpingIcon />,
        color: 'error.main',
        label: 'Dumping',
      };
    case 'traveling':
      return { icon: <MovingIcon />, color: 'primary.main', label: 'Moving' };
    case 'wait':
      return {
        icon: <WaitIcon />,
        color: 'text.secondary',
        label: 'Waiting',
      };
    default:
      return { icon: <div />, color: 'inherit', label: 'Unknown' };
  }
};

export const AssetPlanStep = ({
  setCurrentStep,
}: {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fleetPlanningHandler = new FleetPlanningHandler();
  const authUserEmail = useAppSelector((state) => state?.authReducer?.email);
  const { customerTz, customerCode } = useCustomerInfo();
  const [approvePlanLoading, setApprovePlanLoading] = useState(false);

  const internalUserList = useAppSelector(
    (state) => state.internalUserReducer?.internalUserList
  );
  const externalUserList = useAppSelector(
    (state) => state.externalUserReducer?.externalUserList
  );

  const mlInputPayload = useAppSelector(
    (state) => state.fleetPlanningReducer.mlInputPayload
  );
  const selectedPlanData = useAppSelector((state) =>
    state.fleetPlanningReducer.selectedPlan === 'user'
      ? state.fleetPlanningReducer.userPlan
      : state.fleetPlanningReducer.optimizedPlan
  );

  const selectedPlan = useAppSelector(
    (state) => state.fleetPlanningReducer.selectedPlan
  );

  const todayInTz = useMemo(() => dayjs().tz(customerTz), [customerTz]);

  const { currentShiftLabel } = useMemo(
    () => getCurrentCustomerShiftInfo(customerCode, customerTz),
    [customerCode, customerTz]
  );

  const createdShiftLabel = useMemo(() => {
    const startUtc: string | undefined =
      mlInputPayload?.startDateTime ?? mlInputPayload?.shiftStartDateTime;
    const endUtc: string | undefined =
      mlInputPayload?.endDateTime ?? mlInputPayload?.shiftEndDateTime;

    if (!startUtc || !endUtc || !customerTz) return undefined;

    const startLocal = dayjs.utc(startUtc).tz(customerTz).format('HH:mm');
    const endLocal = dayjs.utc(endUtc).tz(customerTz).format('HH:mm');

    return `Shift ${startLocal} - ${endLocal}`;
  }, [mlInputPayload, customerTz]);

  const selectedUser = useMemo(() => {
    let tempInternalUserList: InternalUser[] = [];
    let tempExternalUserList: ExternalUser[] = [];
    if (Array.isArray(internalUserList)) {
      tempInternalUserList = internalUserList;
    }
    if (Array.isArray(externalUserList)) {
      tempExternalUserList = externalUserList;
    }
    const temp = [...tempInternalUserList, ...tempExternalUserList].find(
      (user) => user.email === authUserEmail
    ) ?? {
      userId: '',
      firstName: '',
      lastName: '',
      email: '',
      isActive: true,
      department: '',
      userCustomerAssociations: [],
    };

    return temp;
  }, [authUserEmail, externalUserList, internalUserList]);

  const { groupedPlanData: aggregatedDataByBumperNumber } = useMemo(() => {
    const collapsed = collapseTravelSegments(selectedPlanData.planData);
    return aggregateSchedulePlanByBumperNumber(collapsed);
  }, [selectedPlanData.planData]);

  const handleExportClick = () => {
    const planName =
      selectedPlan === 'user'
        ? 'plan_schedule.xlsx'
        : 'max_capacity_plan_schedule.xlsx';
    exportPlanDataToExcel(
      selectedPlanData.planData,
      planName,
      'configured',
      customerTz
    );
  };

  const handleApprovePlan = async (planName: string) => {
    try {
      setApprovePlanLoading(true);

      const startDateTime = todayInTz.toISOString();
      const endDateTime = todayInTz.endOf('day').toISOString();
      const startOfDay = todayInTz.startOf('day').toISOString();

      let totalTonnage = 0;
      selectedPlanData.metrics.material_dumped?.forEach(
        (el) => (totalTonnage += el.total)
      );

      dispatch(clearAcknowledgedAssets());
      await fleetPlanningHandler.saveApprovedPlan({
        schedule: selectedPlanData.planData,
        metrics: selectedPlanData.metrics,
        mlInputPayload,
        approvedBy: selectedUser.userId,
        startDateTime,
        endDateTime,
        startOfDay,
        status: 'active',
        planName,
        totalTonnage,
      });
      dispatch(setPlanDispatched(true));
      setCurrentStep(5);
    } catch (error) {
      console.error(error);
    } finally {
      setApprovePlanLoading(false);
    }
  };

  const materialDumpedData = useMemo(() => {
    const temp = mapSchedulePlanMetricsByBumper(
      selectedPlanData.metrics.material_dumped
    );
    return temp;
  }, [selectedPlanData.metrics.trips]);

  const renderLocation = (start?: string, end?: string) => {
    if (!start || !end) return null;
    if (start.toLowerCase() === end.toLowerCase()) {
      return <Typography variant="body2">{formatLocation(start)}</Typography>;
    }
    return (
      <>
        <Typography variant="body2">{formatLocation(start)}</Typography>
        <ArrowIcon sx={{ mx: 0.5, fontSize: '1rem' }} />
        <Typography variant="body2">{formatLocation(end)}</Typography>
      </>
    );
  };

  return (
    <Box
      height={'100%'}
      maxHeight={'100%'}
      overflow="scroll"
      display={'flex'}
      flexDirection={'column'}
      gap={3}
    >
      <Box component="header" display={'flex'} justifyContent={'space-between'}>
        <Box>
          <Typography>{t('cdp_asset_plan_description')}</Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <CustomLoadingButton
            variant="contained"
            color="success"
            onClick={() => {
              const shiftLabel =
                createdShiftLabel ?? currentShiftLabel ?? 'Shift';
              const dateStr = todayInTz.format('DD/MM/YYYY HH:mm:ss');
              const planType =
                selectedPlan === 'user' ? 'Custom' : 'Max Capacity';
              const autoName = `${customerCode} - ${planType} - ${shiftLabel} - ${dateStr}`;
              void handleApprovePlan(autoName);
            }}
            disabled={approvePlanLoading}
            loading={approvePlanLoading}
            startIcon={<TaskAlt />}
            sx={{
              width: '160px',
              height: '36px',
            }}
          >
            {t('cdp_dispatch_plan_button')}
          </CustomLoadingButton>
          <CustomLoadingButton
            variant="contained"
            color="info"
            onClick={handleExportClick}
            startIcon={<Download />}
          >
            {t('export_plan')}
          </CustomLoadingButton>
        </Box>
      </Box>
      {/* <FleetInputsSummary /> */}
      <PlanComparison />
      <Grid container spacing={3}>
        {Object.entries(aggregatedDataByBumperNumber).map(
          ([bumperNumber, actions]) => {
            const assetMaterialDumpedData =
              materialDumpedData[bumperNumber] || {};
            return (
              <Grid item key={bumperNumber} xs={12} sm={6} lg={4}>
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid #334155',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{ px: 2, py: 1, borderBottom: '1px solid #334155' }}
                    display={'flex'}
                    flexDirection={'column'}
                    gap={1}
                  >
                    <Typography
                      variant="body1"
                      component="h5"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {bumperNumber}
                      {(createdShiftLabel ?? currentShiftLabel) && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                          }}
                        >
                          {createdShiftLabel ?? currentShiftLabel}
                        </Box>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: { sm: 'space-between' },
                        alignItems: { sm: 'center' },
                        gap: { xs: 1, sm: 2 },
                        fontSize: '0.75rem', // text-xs equivalent
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{ color: 'text.secondary' }}
                          variant="caption"
                        >
                          {t('cdp_total_label')}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '0.875rem',
                          }}
                        >
                          {actions.filter((a) => a.action === 'dumping').length}
                          <Box
                            component="span"
                            sx={{ fontWeight: 400, color: 'text.secondary' }}
                            fontSize={'10px'}
                          >
                            {t('cdp_trips_unit')}
                          </Box>{' '}
                          /{' '}
                          {truncateTo2dp(
                            safeNumber(assetMaterialDumpedData.total)
                          )}
                          <Box
                            component="span"
                            sx={{ fontWeight: 400, color: 'text.secondary' }}
                            fontSize={'10px'}
                          >
                            {t('cdp_tonnage_unit_short')}
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}
                    height={'450px'}
                  >
                    <List sx={{ p: 0, position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '18px',
                          top: '10px',
                          bottom: '10px',
                          width: '2px',
                          bgcolor: '#475569',
                          zIndex: 0,
                        }}
                      />
                      {actions.map((action, index) => {
                        const details = getActionDetails(action.action);
                        return (
                          <ListItem
                            key={index}
                            disableGutters
                            sx={{ alignItems: 'flex-start', p: 0, mb: 2 }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 0, mr: 2, zIndex: 1, mt: '2px' }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  bgcolor: details.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {React.cloneElement(details.icon, {
                                  sx: { color: 'white', fontSize: '1.125rem' },
                                })}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box
                                  display={'flex'}
                                  alignItems={'center'}
                                  gap={0}
                                  flexWrap={'wrap'}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      color: 'text.primary',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {action.action}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    {`(${convertUtcToDisplayTime(
                                      action.start_ts,
                                      'configured',
                                      customerTz,
                                      'HH:mm'
                                    )} - ${convertUtcToDisplayTime(
                                      action.end_ts,
                                      'configured',
                                      customerTz,
                                      'HH:mm'
                                    )})`}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#64748b',
                                  }}
                                >
                                  {renderLocation(
                                    action.start_location,
                                    action.end_location
                                  )}
                                  {action?.action === 'loading' && (
                                    <Typography variant="body2" ml={1}>
                                      {`(${action?.material_dumped} Ton)`}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            );
          }
        )}
      </Grid>
    </Box>
  );
};
